// Centralized, authoritative metadata for every premium (locked) tool.
// Keyed by toolId, which matches the Dashboard tab value.
// Copy describes ACTUAL functionality only — no fabricated results, no
// live-trading / guaranteed-profit / guaranteed-accuracy claims.
import {
  Link2, Scan, Bookmark, Brain, Swords, Target, Search, LineChart,
  TrendingUp, Repeat, ArrowLeftRight, Sparkles, History, Activity,
} from 'lucide-react';

export const PREMIUM_TOOLS = {
  portfolio: {
    name: 'Portfolio',
    Icon: Link2,
    shortDescription:
      'Connect a read-only brokerage account or enter positions manually to organize and analyze all your options positions in one place.',
    whatItDoes: [
      'Connect Schwab or Public (read-only) or add positions manually',
      'View each position’s legs, strikes, expirations and cost basis',
      'Recalculate position economics with current market data',
      'Send any position straight to the Analyzer, Rollmaster or P/L Chart',
      'Acts as the starting point for every other analysis tool',
    ],
    whyItMatters:
      'Instead of tracking positions in a spreadsheet, you get one organized view you can push into any analysis tool with a click.',
    example:
      'Connect your brokerage (read-only) or add a position such as “1 LUNR 15.50 Call”, then open it in the analyzer or P/L chart.',
    outputDescription:
      'An organized list of your positions with legs, cost basis and quick actions. Read-only — the app cannot place trades or withdraw funds.',
  },
  scanner: {
    name: 'Scanner',
    Icon: Scan,
    shortDescription:
      'Scan a universe of stocks across many options strategies and surface the candidates that currently qualify.',
    whatItDoes: [
      'Scans ~40 stocks across 20 defined options strategies',
      'Applies each strategy’s criteria to every candidate',
      'Uses AI to summarize the qualifying trade picks',
      'Lets you push promising setups into other tools',
      'Syncs selections with your Watchlist',
    ],
    whyItMatters:
      'Rather than checking tickers one by one, you let the scanner surface setups that match the strategy you care about.',
    example:
      'Pick a strategy such as a credit spread and scan the universe to see which symbols currently qualify.',
    outputDescription:
      'A list of qualifying trade candidates per strategy, with the reasoning behind each pick.',
  },
  watchlist: {
    name: 'Watchlist',
    Icon: Bookmark,
    shortDescription:
      'Save and monitor the stocks and strategy setups you care about, with quick access to current market conditions.',
    whatItDoes: [
      'Keep a personal list of stocks and strategy setups',
      'See current market conditions for each entry',
      'Add candidates directly from the Scanner',
      'Jump from a watched item into deeper analysis',
    ],
    whyItMatters:
      'Keeps the setups you’re watching in one place so you can act when conditions line up.',
    example:
      'Add TSLA and NVDA to your watchlist and monitor their conditions alongside your saved strategies.',
    outputDescription:
      'Your saved stocks and strategies with live market context.',
  },
  expert: {
    name: 'Expert',
    Icon: Brain,
    shortDescription:
      'AI-assisted research that grades a stock and suggests setups across technical, fundamental, sentiment and risk dimensions.',
    whatItDoes: [
      'AI stock analysis grades: technical, fundamental, sentiment & risk',
      'Options trade setups with strikes, expiries and entry/exit ideas',
      'Support & resistance and key price levels',
      'Day-trade and swing-trade idea generation',
      'Portfolio health check on your holdings',
    ],
    whyItMatters:
      'Gives you a fast, structured AI read on a name before you commit time to building a position.',
    example:
      'Enter a ticker to get AI grades and a suggested options setup, clearly labelled as AI interpretation.',
    outputDescription:
      'AI-generated grades and setup ideas — AI interpretation, not deterministic math or trade execution.',
  },
  mentor: {
    name: 'Mentor',
    Icon: Swords,
    shortDescription:
      'An AI trading mentor that reviews your plan and gives blunt, persona-driven coaching and critique.',
    whatItDoes: [
      'Choose a mentor persona (e.g. Drill Sergeant, The Professor, The Surgeon)',
      'Get candid feedback on your trade idea or plan',
      'Surface risks and discipline gaps in your approach',
      'AI-assisted guidance tailored to the persona you pick',
    ],
    whyItMatters:
      'Acts like a tough second opinion that pushes back on weak reasoning before you risk capital.',
    example:
      'Pick a persona and describe a trade; the mentor critiques your plan and highlights the risks.',
    outputDescription:
      'Persona-styled AI coaching and critique — guidance only, not financial advice or guaranteed outcomes.',
  },
  movement: {
    name: 'Movement',
    Icon: Target,
    shortDescription:
      'Analyze a stock’s expected price movement and options flow to gauge how much it may move by expiration.',
    whatItDoes: [
      'Estimate the expected move by expiration',
      'Review options-flow context for the symbol',
      'See movement framed around the nearest expirations',
      'Ground strategy choices in realistic move expectations',
    ],
    whyItMatters:
      'Helps you size strikes and expirations around how far the stock is actually expected to move.',
    example:
      'Enter a symbol to see its expected move range into the next expiration.',
    outputDescription:
      'Expected-move estimates and options-flow context for the symbol.',
  },
  analysis: {
    name: 'Stock Analysis',
    Icon: Search,
    shortDescription:
      'Search any stock to view market data, its live option chain and AI strategy interpretation in one place.',
    whatItDoes: [
      'Look up stock details and current market data',
      'Load the live option chain for the symbol',
      'Run the strategy analyzer on selected options',
      'Get AI strategy commentary alongside the data',
    ],
    whyItMatters:
      'A single workspace to go from a ticker to a concrete options strategy idea.',
    example:
      'Search AAPL to see its data, pull the option chain and visualize a strategy.',
    outputDescription:
      'Stock data, an option chain and AI interpretation — AI commentary is kept separate from deterministic figures.',
  },
  plchart: {
    name: 'P/L Chart',
    Icon: LineChart,
    shortDescription:
      'Visualize the potential profit and loss of an options position across a range of underlying prices.',
    whatItDoes: [
      'Maps potential P/L across underlying prices',
      'Shows breakeven point(s)',
      'Displays maximum profit/loss where applicable',
      'Visualizes the expiration payoff',
      'Supports multi-leg option positions',
    ],
    whyItMatters:
      'Instead of judging a position from a few numbers, you see how it behaves as the underlying moves.',
    example:
      'Enter a position such as “1 LUNR 15.50 Call at $0.53 entry” to see its P/L profile across prices.',
    outputDescription:
      'An interactive P/L payoff chart with breakevens and max profit/loss.',
  },
  manual: {
    name: 'My Position',
    Icon: TrendingUp,
    shortDescription:
      'Enter an existing single- or multi-leg options position and get deterministic calculations plus AI interpretation.',
    whatItDoes: [
      'Enter single or multi-leg positions by hand',
      'Compute deterministic position metrics',
      'See breakevens, max profit/loss and cost basis',
      'Add AI-assisted interpretation of the position',
    ],
    whyItMatters:
      'Lets you stress-test a position you already hold or are considering, with real math.',
    example:
      'Enter “1 LUNR 15.50 Call, $0.53 entry” to see its calculated risk profile.',
    outputDescription:
      'Deterministic position calculations plus optional AI interpretation.',
  },
  rollmaster: {
    name: 'Rollmaster',
    Icon: Repeat,
    shortDescription:
      'Evaluate rolling an existing options position across candidate expirations and strikes using live market data.',
    whatItDoes: [
      'Enter your current single or multi-leg position',
      'Scan candidate expirations and strikes to roll into',
      'Use market data and deterministic calculations to compare',
      'Identify roll candidates worth a closer look',
    ],
    whyItMatters:
      'Helps you decide whether and where to roll a position instead of guessing.',
    example:
      'Load a position nearing expiration and review roll candidates across later expirations.',
    outputDescription:
      'Roll candidates with the market data and calculations behind them.',
  },
  rollcalc: {
    name: 'Roll Calc',
    Icon: ArrowLeftRight,
    shortDescription:
      'Compare a position you hold against a specific roll target and calculate the economics of closing one and opening the other.',
    whatItDoes: [
      'Enter the position you currently hold',
      'Enter the position you want to roll into',
      'Calculate the net cost/credit of the roll',
      'Compare the economics side by side',
    ],
    whyItMatters:
      'Gives you the exact numbers of a specific roll before you place it.',
    example:
      'Compare holding a near-dated call vs rolling to a later expiration to see the net debit/credit.',
    outputDescription:
      'A side-by-side roll comparison with net cost/credit and key economics.',
  },
  strategyfinder: {
    name: 'AI Finder',
    Icon: Sparkles,
    shortDescription:
      'Enter a stock and let a multi-AI workflow recommend option strategies suited to current conditions.',
    whatItDoes: [
      'Analyze a symbol with a multi-AI workflow',
      'Recommend option strategies for current conditions',
      'Show probability-of-profit style context per idea',
      'Explain why each strategy was suggested',
    ],
    whyItMatters:
      'A fast way to discover candidate strategies for a name you’re interested in.',
    example:
      'Enter a symbol and the finder suggests strategies with the reasoning behind each.',
    outputDescription:
      'A ranked set of suggested strategies with context — AI suggestions, not guaranteed outcomes.',
  },
  history: {
    name: 'History',
    Icon: History,
    shortDescription:
      'Review your previous strategy analyses so you can revisit and compare earlier work.',
    whatItDoes: [
      'See a list of your past strategy analyses',
      'Revisit the details of earlier analyses',
      'Re-open a past analysis to continue or compare',
      'Track how your thinking evolved over time',
    ],
    whyItMatters:
      'Keeps a record of what you’ve analyzed so you don’t repeat work or lose ideas.',
    example:
      'Open History to reopen a strategy you analyzed earlier this week.',
    outputDescription:
      'A chronological list of your saved analyses.',
  },
  metrics: {
    name: 'API Monitor',
    Icon: Activity,
    shortDescription:
      'Monitor the app’s API activity, usage and any errors so you can see how market data is being fetched.',
    whatItDoes: [
      'View recorded API call metrics',
      'See error counts and recent issues',
      'Confirm market-data and AI calls are flowing correctly',
      'Track usage as you work in the app',
    ],
    whyItMatters:
      'Gives transparency into the data calls powering your analysis and surfaces any hiccups.',
    example:
      'Open API Monitor to review recent calls and confirm data is flowing without errors.',
    outputDescription:
      'API usage and error metrics for the current app activity.',
  },
};

export const getPremiumTool = (toolId) => PREMIUM_TOOLS[toolId] || null;
